import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppMsg } from 'src/features/app/app.actions';
import { HeaderVariant } from 'src/components/Header/HeaderVariant';
import { VatProvider } from 'src/components/AppShell/VatProvider';
import { BrandSchema } from 'src/components/AppShell/BrandStructuredData';
import { CspHeader } from 'src/components/CustomerServicePortal/CspHeader';

import { Splitio } from '../Splitio/Splitio';
import { MainProvider } from '../Main/Main';
import { PopupBanner } from '../PopupBanner/PopupBanner';
import { PopupPageProvider } from '../PopupBanner/PopupPageContext';
import { useDeps } from '../../hooks/useDeps';
import { StoreState } from '../../types/global-types';
import { OutdatedModal } from '../Outdated/Outdated';
import { Scripts } from '../Scripts/Scripts';
import { FooterSlot } from '../Footer/FooterSlot';

import { Head } from './Head';
import { ConfigProvider } from './ConfigProvider';
import { OfflineRibon } from './OfflineRibon';

const InlineSvgs = React.lazy(() => import(/* webpackChunkName:"dev-svg-icons" */ './InlineSvg'));

// Flips renderEnv after hydration commits, so the first client render matches the server's.
function useMarkBrowserRendered() {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(AppMsg('App.SetRenderEnv', 'browser'));
    }, [dispatch]);
}

export const AppShell: React.FC<React.PropsWithChildren> = React.memo((props) => {
    const deps = useDeps();
    useMarkBrowserRendered();

    return (
        <Splitio>
            <ConfigProvider>
                <BrandSchema />
                <VatProvider>
                    <PopupPageProvider>
                        <Head />
                        {deps.env.NODE_ENV !== 'production' && (
                            <React.Suspense fallback={null}>
                                <InlineSvgs />
                            </React.Suspense>
                        )}
                        <CspHeader />
                        <HeaderVariant />
                        <MainProvider>
                            <OfflineOutdated />
                            {props.children}
                        </MainProvider>
                        <FooterSlot />
                        <PopupBanner />
                        <Scripts />
                    </PopupPageProvider>
                </VatProvider>
            </ConfigProvider>
        </Splitio>
    );
});

function stateSelector(state: StoreState) {
    return {
        outdated: state.runtime.outdated,
        online: state.runtime.online,
    };
}
function OfflineOutdated() {
    const state = useSelector(stateSelector, shallowEqual);
    return (
        <>
            {state.outdated && <OutdatedModal />}
            {!state.online && <OfflineRibon />}
        </>
    );
}
